import React, { useState, useRef } from 'react';
import { X, Upload, Image as ImageIcon, Check, RefreshCw, Trash2, Eye, Sparkles, ArrowRight, Layout, SlidersHorizontal } from 'lucide-react';
import { BannerSlide } from '../types';
import { DEFAULT_BANNER_SLIDES } from '../data/banners';

interface BannerUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  slides: BannerSlide[];
  onSaveSlides: (updated: BannerSlide[]) => void;
  onResetSlides: () => void;
}

export const BannerUploadModal: React.FC<BannerUploadModalProps> = ({
  isOpen,
  onClose,
  slides,
  onSaveSlides,
  onResetSlides,
}) => {
  const [localSlides, setLocalSlides] = useState<BannerSlide[]>(() => slides);
  const [activeTab, setActiveTab] = useState<number>(0);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadSuccessToast, setUploadSuccessToast] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Sync with prop when opened
  React.useEffect(() => {
    if (isOpen) {
      setLocalSlides(slides);
    }
  }, [isOpen, slides]);

  if (!isOpen) return null;

  const currentSlide = localSlides[activeTab] || localSlides[0];
  const fitMode = currentSlide?.fitMode || 'cover';
  const showTextOverlay = currentSlide?.showTextOverlay !== false;

  const handleUpdateCurrentSlide = (field: keyof BannerSlide, value: any) => {
    setLocalSlides((prev) =>
      prev.map((s, idx) => (idx === activeTab ? { ...s, [field]: value } : s))
    );
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Check size limit (under 20MB)
    if (file.size > 20 * 1024 * 1024) {
      alert('ขนาดไฟล์ภาพต้องไม่เกิน 20MB');
      return;
    }

    setIsUploading(true);
    const reader = new FileReader();
    reader.onload = async (event) => {
      const rawBase64 = event.target?.result as string;
      const targetSlide = localSlides[activeTab];

      try {
        // Upload directly to server storage bucket
        const res = await fetch('/api/sliders/upload', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            imageBase64: rawBase64,
            slideId: targetSlide?.id,
            slideIndex: activeTab,
            title: targetSlide?.title
          })
        });

        const data = await res.json();
        const finalUrl = (res.ok && data.success && data.url) ? data.url : rawBase64;

        const updatedSlides = localSlides.map((s, idx) =>
          idx === activeTab ? { ...s, imageUrl: finalUrl } : s
        );
        setLocalSlides(updatedSlides);
        onSaveSlides(updatedSlides); // Instant auto-save to server & cloud

        setIsUploading(false);
        setUploadSuccessToast(true);
        setTimeout(() => setUploadSuccessToast(false), 3500);
      } catch (uploadErr) {
        console.warn('Storage bucket upload fallback:', uploadErr);
        const updatedSlides = localSlides.map((s, idx) =>
          idx === activeTab ? { ...s, imageUrl: rawBase64 } : s
        );
        setLocalSlides(updatedSlides);
        onSaveSlides(updatedSlides);
        setIsUploading(false);
        setUploadSuccessToast(true);
        setTimeout(() => setUploadSuccessToast(false), 3500);
      }
    };
    reader.onerror = () => {
      alert('เกิดข้อผิดพลาดในการอ่านไฟล์ภาพ');
      setIsUploading(false);
    };
    reader.readAsDataURL(file);
  };

  const handleApplyPreset = (preset: Partial<BannerSlide>) => {
    const updatedSlides = localSlides.map((s, idx) =>
      idx === activeTab ? { ...s, ...preset } : s
    );
    setLocalSlides(updatedSlides);
    onSaveSlides(updatedSlides);
    setUploadSuccessToast(true);
    setTimeout(() => setUploadSuccessToast(false), 3000);
  };

  const handleSave = () => {
    onSaveSlides(localSlides);
    onClose();
  };

  const activeSlidesCount = localSlides.filter((s) => s.isActive).length;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-slate-900/60 backdrop-blur-xs animate-fadeIn">
      <div className="bg-white w-full max-w-3xl rounded-2xl shadow-2xl border border-slate-200 overflow-hidden flex flex-col max-h-[92vh]">
        {/* Modal Header */}
        <div className="px-5 py-4 bg-gradient-to-r from-blue-700 to-blue-600 text-white flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-white/15 flex items-center justify-center">
              <Upload className="w-4 h-4 text-white" />
            </div>
            <div>
              <h3 className="text-base font-bold tracking-tight">
                จัดการและปรับแต่งสไลเดอร์แบนเนอร์ (2-3 สไลด์)
              </h3>
              <p className="text-xs text-blue-100">
                สถาปัตยกรรมแสดงผลพอดีภาพ ไม่ตัดขอบ รองรับทั้งภาพกราฟิกสำเร็จรูปและภาพสินค้า (เปิดใช้งาน {activeSlidesCount} สไลด์)
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full hover:bg-white/20 flex items-center justify-center text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Slide Selection Tabs */}
        <div className="flex items-center gap-2 px-5 py-3 bg-slate-50 border-b border-slate-200">
          <span className="text-xs font-semibold text-slate-500 mr-1">เลือกสไลด์:</span>
          {localSlides.map((s, idx) => (
            <button
              key={s.id}
              onClick={() => setActiveTab(idx)}
              className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl text-xs font-semibold transition-all ${
                activeTab === idx
                  ? 'bg-blue-600 text-white shadow-xs'
                  : 'bg-white text-slate-600 hover:bg-slate-200/70 border border-slate-200'
              }`}
            >
              <span>สไลด์ที่ {idx + 1}</span>
              {s.isActive ? (
                <span
                  className={`w-2 h-2 rounded-full ${
                    activeTab === idx ? 'bg-emerald-300' : 'bg-emerald-500'
                  }`}
                  title="เปิดใช้งาน"
                />
              ) : (
                <span className="text-[10px] opacity-70">(ปิด)</span>
              )}
            </button>
          ))}

          <div className="ml-auto text-xs text-slate-500">
            {activeSlidesCount < 2 ? (
              <span className="text-amber-600 font-medium">ควรเปิดใช้งานอย่างน้อย 2 สไลด์</span>
            ) : (
              <span className="text-emerald-600 font-medium">เปิดใช้งาน {activeSlidesCount} สไลด์</span>
            )}
          </div>
        </div>

        {/* Modal Body */}
        <div className="p-5 overflow-y-auto space-y-5 flex-1">
          {uploadSuccessToast && (
            <div className="p-3 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs font-semibold flex items-center gap-2 animate-fadeIn">
              <Check className="w-4 h-4 text-emerald-600 shrink-0" />
              อัปโหลดรูปภาพสำหรับสไลด์ที่ {activeTab + 1} สำเร็จแล้ว!
            </div>
          )}

          {/* Slide Settings Grid (Active & Fit Architecture & Text Overlay) */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {/* Status Toggle */}
            <div className="p-3.5 rounded-xl bg-slate-50 border border-slate-200 flex items-center justify-between">
              <div>
                <div className="text-xs font-bold text-slate-800">สถานะสไลด์ที่ {activeTab + 1}</div>
                <div className="text-[11px] text-slate-500">เปิดแสดงในหน้าเว็บ</div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={currentSlide?.isActive ?? true}
                  onChange={(e) => handleUpdateCurrentSlide('isActive', e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-10 h-5 bg-slate-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </div>

            {/* Fit Mode Selector */}
            <div className="p-3.5 rounded-xl bg-slate-50 border border-slate-200">
              <div className="text-xs font-bold text-slate-800 mb-1.5 flex items-center gap-1">
                <Layout className="w-3.5 h-3.5 text-blue-600" />
                การจัดวางรูปภาพ (Fit Mode)
              </div>
              <div className="grid grid-cols-2 gap-1.5">
                <button
                  type="button"
                  onClick={() => handleUpdateCurrentSlide('fitMode', 'contain')}
                  className={`py-1 px-2 rounded-lg text-xs font-semibold transition-all border ${
                    fitMode === 'contain'
                      ? 'bg-blue-600 text-white border-blue-600 shadow-xs'
                      : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-100'
                  }`}
                  title="แสดงภาพครบผืน 100% ไม่ตัดขอบ"
                >
                  พอดีภาพ (Contain)
                </button>
                <button
                  type="button"
                  onClick={() => handleUpdateCurrentSlide('fitMode', 'cover')}
                  className={`py-1 px-2 rounded-lg text-xs font-semibold transition-all border ${
                    fitMode === 'cover'
                      ? 'bg-blue-600 text-white border-blue-600 shadow-xs'
                      : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-100'
                  }`}
                  title="ครอบเต็มกรอบ"
                >
                  เต็มกรอบ (Cover)
                </button>
              </div>
            </div>

            {/* Text Overlay Toggle */}
            <div className="p-3.5 rounded-xl bg-slate-50 border border-slate-200 flex items-center justify-between">
              <div>
                <div className="text-xs font-bold text-slate-800">ข้อความทับบนภาพ</div>
                <div className="text-[11px] text-slate-500">
                  {showTextOverlay ? 'เปิด (แสดงข้อความ)' : 'ปิด (เน้นภาพล้วน 100%)'}
                </div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={showTextOverlay}
                  onChange={(e) => handleUpdateCurrentSlide('showTextOverlay', e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-10 h-5 bg-slate-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </div>
          </div>

          {/* Current Banner Live Preview */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-bold text-slate-700 flex items-center gap-1.5">
                <Eye className="w-3.5 h-3.5 text-blue-600" />
                ตัวอย่างการแสดงผลสไลด์ที่ {activeTab + 1} ({fitMode === 'contain' ? 'โหมดพอดีภาพ 100%' : 'โหมดเต็มกรอบ'})
              </label>
            </div>
            
            <div className="relative rounded-2xl overflow-hidden border border-slate-200 aspect-[21/10] sm:aspect-[24/10.5] bg-white shadow-inner flex items-center justify-between">
              {currentSlide?.imageUrl ? (
                <img
                  src={currentSlide.imageUrl}
                  alt={currentSlide.title}
                  className={`relative z-1 w-full h-full ${
                    fitMode === 'contain' ? 'object-contain object-center' : 'object-cover object-center'
                  }`}
                />
              ) : null}

              {/* Text overlay only if enabled */}
              {showTextOverlay && (
                <div className="relative z-10 max-w-sm m-3 sm:m-5 bg-white/85 backdrop-blur-md p-3.5 sm:p-5 rounded-2xl border border-white/90 shadow-lg space-y-1 text-slate-800">
                  {currentSlide?.badge && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gradient-to-r from-rose-500 to-[#E60067] text-white text-[10px] font-bold">
                      <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
                      {currentSlide.badge}
                    </span>
                  )}
                  <h4 className="text-sm sm:text-base font-extrabold text-slate-800 leading-tight">
                    {currentSlide?.title || 'หัวข้อแบนเนอร์กิฟฟารีน'}
                  </h4>
                  <p className="text-[10px] sm:text-xs text-slate-600 line-clamp-2">
                    {currentSlide?.subtitle || 'รายละเอียดผลิตภัณฑ์และข้อความโปรโมชั่น'}
                  </p>
                  {currentSlide?.memberPrice && (
                    <div className="text-xs font-black text-[#E60067] pt-0.5">
                      ราคาสมาชิก ฿{currentSlide.memberPrice} {currentSlide.price ? `(ปกติ ฿${currentSlide.price})` : ''}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Upload Method: File Upload or Image URL */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Direct File Upload */}
            <div className="p-4 rounded-xl border-2 border-dashed border-slate-300 hover:border-blue-500 bg-slate-50 hover:bg-blue-50/40 transition-colors text-center flex flex-col items-center justify-center">
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                accept="image/*"
                className="hidden"
              />
              <div className="w-10 h-10 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center mb-2">
                <Upload className="w-5 h-5" />
              </div>
              <div className="text-xs font-bold text-slate-700">
                {isUploading ? 'กำลังประมวลผลรูปภาพ...' : 'อัปโหลดภาพจากเครื่อง (PNG, JPG, WebP)'}
              </div>
              <p className="text-[11px] text-slate-500 mt-1 mb-3">
                สัดส่วนที่แนะนำ: 16:9, 21:9 หรือ 3:1 (สไลเดอร์จะปรับขนาดพอดีภาพอัตโนมัติ)
              </p>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploading}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs font-semibold transition-all shadow-xs"
              >
                {isUploading ? 'กำลังอัปโหลด...' : 'เลือกไฟล์รูปภาพ'}
              </button>
            </div>

            {/* Image URL Input */}
            <div className="p-4 rounded-xl border border-slate-200 bg-white space-y-3">
              <div className="flex items-center justify-between">
                <label className="block text-xs font-bold text-slate-700 flex items-center gap-1.5">
                  <ImageIcon className="w-3.5 h-3.5 text-blue-600" />
                  <span>วางลิงก์รูปภาพปลายทาง (Image URL)</span>
                </label>
                <span className="text-[10px] bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full font-bold">Auto-Save ทันที</span>
              </div>
              <input
                type="url"
                value={currentSlide?.imageUrl || ''}
                onChange={(e) => {
                  const val = e.target.value;
                  handleUpdateCurrentSlide('imageUrl', val);
                  // Auto-save immediately if valid URL or image path
                  if (val.trim()) {
                    const updated = localSlides.map((s, idx) =>
                      idx === activeTab ? { ...s, imageUrl: val.trim() } : s
                    );
                    onSaveSlides(updated);
                  }
                }}
                onBlur={(e) => {
                  const val = e.target.value.trim();
                  if (val) {
                    const updated = localSlides.map((s, idx) =>
                      idx === activeTab ? { ...s, imageUrl: val } : s
                    );
                    onSaveSlides(updated);
                    setUploadSuccessToast(true);
                    setTimeout(() => setUploadSuccessToast(false), 2500);
                  }
                }}
                placeholder="วางลิงก์รูปภาพ เช่น https://example.com/banner.jpg"
                className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs focus:bg-white focus:border-blue-500 focus:outline-none font-mono"
              />
              <p className="text-[11px] text-slate-500">
                แค่วางลิงก์ ระบบจะดึงรูปภาพมาแสดงและบันทึกลงระบบทันที
              </p>

              {/* Quick Preset Buttons */}
              <div className="pt-2 border-t border-slate-100">
                <span className="text-[11px] font-semibold text-slate-600 block mb-1.5">
                  เลือกภาพสำเร็จรูปกิฟฟารีน:
                </span>
                <div className="flex flex-wrap gap-1.5">
                  <button
                    type="button"
                    onClick={() =>
                      handleApplyPreset({
                        imageUrl: 'https://images.unsplash.com/photo-1620916566398-39f1143ab7be?auto=format&fit=crop&w=1400&q=80',
                        title: 'เรตินอยด์ โดสถึง แบบไม่ทำร้ายผิว',
                        subtitle: 'พรีเมียม เอชพีอาร์ เรตินอยด์ สกิน บูสเตอร์',
                        badge: 'HPR 0.2%',
                        productCode: '84054',
                        price: 780,
                        memberPrice: 585,
                        pv: 585,
                      })
                    }
                    className="px-2.5 py-1 bg-purple-50 text-purple-700 hover:bg-purple-100 rounded-lg text-[10.5px] font-medium transition-colors"
                  >
                    ✨ เรตินอยด์ HPR
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      handleApplyPreset({
                        imageUrl: 'https://images.unsplash.com/photo-1556228720-195a672e8a03?auto=format&fit=crop&w=1400&q=80',
                        title: 'ผิวฉ่ำวาว อิ่มฟู ดูโกลว์ใส',
                        subtitle: 'กิฟฟารีน ไฮยา พรี-ซีรั่ม ไฮยาลูรอนบริสุทธิ์จากเยอรมนี',
                        badge: 'BEST SELLER',
                        productCode: '10032',
                        price: 600,
                        memberPrice: 450,
                        pv: 450,
                      })
                    }
                    className="px-2.5 py-1 bg-blue-50 text-blue-700 hover:bg-blue-100 rounded-lg text-[10.5px] font-medium transition-colors"
                  >
                    💧 ไฮยา พรี-ซีรั่ม
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      handleApplyPreset({
                        imageUrl: 'https://images.unsplash.com/photo-1584308666744-24d5c474f2ae?auto=format&fit=crop&w=1400&q=80',
                        title: 'ดูแลหัวใจและสมอง โอเมก้า 3 เข้มข้น',
                        subtitle: 'กิฟฟารีน น้ำมันปลา 4X มาตรฐานสากล',
                        badge: 'HEALTH CARE',
                        productCode: '40111',
                        price: 820,
                        memberPrice: 620,
                        pv: 620,
                      })
                    }
                    className="px-2.5 py-1 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 rounded-lg text-[10.5px] font-medium transition-colors"
                  >
                    🌿 น้ำมันปลา 4X
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Text & Details Configuration (Collapsed if showTextOverlay is false) */}
          {showTextOverlay && (
            <div className="p-4 rounded-xl border border-slate-200 bg-slate-50 space-y-3 animate-fadeIn">
              <h4 className="text-xs font-bold text-slate-800 flex items-center gap-1.5">
                <Sparkles className="w-3.5 h-3.5 text-amber-500" />
                ข้อความบนแบนเนอร์
              </h4>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11px] font-medium text-slate-600 mb-1">
                    หัวข้อหลัก (Headline)
                  </label>
                  <input
                    type="text"
                    value={currentSlide?.title || ''}
                    onChange={(e) => handleUpdateCurrentSlide('title', e.target.value)}
                    placeholder="เช่น เรตินอยด์ โดสถึง แบบไม่ทำร้ายผิว"
                    className="w-full px-3 py-1.5 bg-white border border-slate-200 rounded-lg text-xs focus:border-blue-500 focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-[11px] font-medium text-slate-600 mb-1">
                    ป้ายกำกับ (Badge เช่น HPR 0.2%, NEW, SALE)
                  </label>
                  <input
                    type="text"
                    value={currentSlide?.badge || ''}
                    onChange={(e) => handleUpdateCurrentSlide('badge', e.target.value)}
                    placeholder="HPR 0.2%"
                    className="w-full px-3 py-1.5 bg-white border border-slate-200 rounded-lg text-xs focus:border-blue-500 focus:outline-none"
                  />
                </div>

                <div className="sm:col-span-2">
                  <label className="block text-[11px] font-medium text-slate-600 mb-1">
                    ข้อความรอง / รายละเอียดสินค้า (Subtitle)
                  </label>
                  <input
                    type="text"
                    value={currentSlide?.subtitle || ''}
                    onChange={(e) => handleUpdateCurrentSlide('subtitle', e.target.value)}
                    placeholder="เช่น พรีเมียม เอชพีอาร์ เรตินอยด์ สกิน บูสเตอร์ รหัส 84054"
                    className="w-full px-3 py-1.5 bg-white border border-slate-200 rounded-lg text-xs focus:border-blue-500 focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-[11px] font-medium text-slate-600 mb-1">
                    รหัสสินค้ากิฟฟารีน (Product Code)
                  </label>
                  <input
                    type="text"
                    value={currentSlide?.productCode || ''}
                    onChange={(e) => handleUpdateCurrentSlide('productCode', e.target.value)}
                    placeholder="84054"
                    className="w-full px-3 py-1.5 bg-white border border-slate-200 rounded-lg text-xs font-mono focus:border-blue-500 focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-[11px] font-medium text-slate-600 mb-1">
                    ราคาสมาชิก
                  </label>
                  <div>
                    <input
                      type="number"
                      value={currentSlide?.memberPrice || ''}
                      onChange={(e) => handleUpdateCurrentSlide('memberPrice', Number(e.target.value))}
                      placeholder="ราคาสมาชิก 585"
                      className="w-full px-3 py-1.5 bg-white border border-slate-200 rounded-lg text-xs focus:border-blue-500 focus:outline-none"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="px-5 py-3.5 bg-slate-100 border-t border-slate-200 flex flex-wrap items-center justify-between gap-3">
          <button
            type="button"
            onClick={() => {
              if (confirm('คุณต้องการรีเซ็ตแบนเนอร์ทั้งหมดกลับเป็นค่าเริ่มต้นใช่หรือไม่?')) {
                onResetSlides();
                setLocalSlides(DEFAULT_BANNER_SLIDES);
              }
            }}
            className="text-xs text-slate-500 hover:text-red-600 font-medium flex items-center gap-1.5 transition-colors"
          >
            <RefreshCw className="w-3.5 h-3.5" />
            รีเซ็ตค่าเริ่มต้น
          </button>

          <div className="flex items-center gap-2.5">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-200 rounded-xl transition-colors"
            >
              ยกเลิก
            </button>
            <button
              type="button"
              onClick={handleSave}
              className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold rounded-xl shadow-xs transition-all flex items-center gap-1.5"
            >
              <Check className="w-4 h-4" />
              บันทึกและแสดงผลทันที
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
